import type { TpslOrderType, TriggerPriceType } from '@flipster/apis/v2/trade/types';
import { triggerPriceTypeEnum } from '@flipster/apis/v2/trade/types';
import type { PswapOrder } from '@flipster/r230522/model/prex.v2.r230522.tables.private_orders';
import Num, { BigNumber } from '@flipster/utils/num';

/**
 * 기존 Order/Position TP/SL "편집(Edit)" 진입 시, 선택된 WS 주문(row)의 leg 필드를
 * 모달 입력 초기값(prefill)으로 변환하는 순수 파생 로직. (selectedTpslOrderBunja 에서 추출)
 *
 * 두 row 형태:
 * - open(standalone Position TP/SL row): row 자체가 TP/SL → row-level 필드 사용
 *   (`orderType` STOP_LIMIT/STOP_MARKET, `price` = limit price, `priceMatchStrategy` = BBO 마커)
 * - pending(Trigger/Limit 주문 + bracket TP/SL): 접두 필드(`orderTp*` / `orderSl*`) 사용
 *
 * 미선택(create 모드)이면 undefined/null → 모달이 기본값으로 폴백.
 */

export type TpslLegType = 'tp' | 'sl';
export type TpslPositionType = 'open' | 'pending';

/** 선택 주문의 trigger price type. 미선택/미저장이면 undefined → 모달 기본값(Last) 폴백. */
export const deriveTpslTriggerPriceType = (
  order: PswapOrder | undefined,
  positionType: TpslPositionType,
  tpslType: TpslLegType,
): TriggerPriceType | undefined => {
  if (!order) return undefined;
  switch (positionType) {
    case 'open':
      return order.triggerPriceType;
    case 'pending':
      return tpslType === 'tp' ? order.orderTpTriggerPriceType : order.orderSlTriggerPriceType;
    default:
      return undefined;
  }
};

/** Edit: selected bracket type → create: pending parent type → Last. */
export const resolveInitialTpslTriggerPriceType = (
  selectedType: TriggerPriceType | undefined,
  pendingParent: PswapOrder | undefined,
): TriggerPriceType => selectedType ?? pendingParent?.triggerPriceType ?? triggerPriceTypeEnum.last;

/** 선택 주문의 실행 타입(Market/Limit). 알 수 없으면 undefined → 모달 기본값 폴백. */
export const deriveTpslOrderType = (
  order: PswapOrder | undefined,
  positionType: TpslPositionType,
  tpslType: TpslLegType,
): TpslOrderType | undefined => {
  if (!order) return undefined;
  switch (positionType) {
    case 'open':
      return order.orderType === 'ORDER_TYPE_STOP_LIMIT' ? 'Limit' : 'Market';
    case 'pending': {
      const raw = tpslType === 'tp' ? order.orderTpOrderType : order.orderSlOrderType;
      if (raw === 'TPSL_ORDER_TYPE_LIMIT') return 'Limit';
      if (raw === 'TPSL_ORDER_TYPE_MARKET') return 'Market';
      // Null / Invalid → unknown.
      return undefined;
    }
    default:
      return undefined;
  }
};

/** 선택 주문의 trigger price. 미선택이면 null. */
export const deriveTpslTriggerPrice = (
  order: PswapOrder | undefined,
  positionType: TpslPositionType,
  tpslType: TpslLegType,
) => {
  if (!order) return null;
  switch (positionType) {
    case 'open':
      return order.triggerPrice;
    case 'pending':
      return tpslType === 'tp' ? order.orderTpTriggerPrice : order.orderSlTriggerPrice;
    default:
      return null;
  }
};

/** 선택 주문의 limit price. 미선택/미설정이면 undefined. */
export const deriveTpslLimitPrice = (
  order: PswapOrder | undefined,
  positionType: TpslPositionType,
  tpslType: TpslLegType,
): BigNumber | undefined => {
  if (!order) return undefined;
  switch (positionType) {
    case 'open':
      return order.price;
    case 'pending':
      return (tpslType === 'tp' ? order.orderTpLimitPrice : order.orderSlLimitPrice) ?? undefined;
    default:
      return undefined;
  }
};

/**
 * 선택 주문이 BBO 인지.
 * - Limit 이 아니면 false.
 * - pending: 접두 `priceMatchStrategy` 유무로 판정.
 * - open standalone: row-level `priceMatchStrategy` 가 있으면 true,
 *   없으면 레거시 휴리스틱("Limit + limit price 미저장" → 트리거 시점 stamping 전이므로 BBO).
 */
export const deriveTpslIsBbo = (
  order: PswapOrder | undefined,
  positionType: TpslPositionType,
  tpslType: TpslLegType,
): boolean => {
  if (deriveTpslOrderType(order, positionType, tpslType) !== 'Limit') return false;
  if (order && positionType === 'pending') {
    const strategy = tpslType === 'tp' ? order.orderTpPriceMatchStrategy : order.orderSlPriceMatchStrategy;
    return Boolean(strategy);
  }
  if (order?.priceMatchStrategy) return true;
  const limitPrice = deriveTpslLimitPrice(order, positionType, tpslType);
  return !limitPrice || limitPrice.isZero();
};

/**
 * 선택 주문의 qty 가 현재 포지션 수량의 몇 % 인지 (표시용 정수, half-up).
 *
 * partial TP/SL 의 qty 는 포지션이 partial close 로 줄어도 서버가 줄여주지 않는다. entire 만
 * 자동 스케일링되고, partial 은 사용자가 입력한 qty 를 그대로 유지하는 것이 정책이다(Bitget/OKX 동일).
 * 따라서 이 비율은 100% 를 넘을 수 있고, 목록은 넘은 값을 그대로 노출한다(0.75 BTC / 150%).
 * 100% 상한이 필요한 곳(폼 슬라이더 prefill)은 소비 측에서 clamp 한다.
 *
 * ref: QA-21301 / https://prextech.slack.com/archives/C01JGTJHR8E/p1785731092645229
 */
export const deriveTpslQtyPercent = (
  orderQty: BigNumber | string | undefined,
  positionQty: BigNumber | string | undefined,
): number => {
  const qty = Num(orderQty || 0);
  const total = Num(positionQty || 0);
  if (qty.isZero() || total.isZero()) return 0;
  const percent = qty.dividedBy(total).multipliedBy(100).integerValue(BigNumber.ROUND_HALF_UP).toNumber();
  return Math.max(0, percent);
};
